/**
 * The three screens the original shows as a card floating over the dashboard:
 * New Leads, Clients, My Account.
 *
 * ── Why this is here and not in a feature ─────────────────────────────────
 *
 * Because it is composition. Putting the dashboard behind Clients would mean
 * `features/clients` importing `features/dashboard`, which ARCHITECTURE.md §3
 * forbids for good reason — the moment two features know about each other,
 * neither can be moved, tested or deleted on its own. This file sits beside
 * `main.js`, in the composition root, where knowing about several features is
 * the job rather than a leak.
 *
 * It exists at all because three routes had the same fifteen lines and
 * `main.js` ran out of its 400-line budget on the third.
 *
 * ── Order is the whole correctness argument ───────────────────────────────
 *
 * The dashboard mounts FIRST, synchronously, because `mountDashboardScreen`
 * calls `replace(root, …)` and owns the host element. The overlay screens
 * append to it. Get that backwards and the dashboard erases the modal on its
 * way in — which is why both mounts are passed in already-resolved rather than
 * imported here: an `await` between them is exactly the bug this comment is
 * about.
 *
 * Teardown runs the other way round: the overlay leaves before the thing it
 * was floating over, so there is never a frame showing a modal with nothing
 * behind it.
 */

/**
 * @typedef {(host: HTMLElement|null, options?: {notify?: (m: string, t?: string) => void}) => () => void} ScreenMount
 */

/**
 * Build the shell's `mount` callback for one of the three overlay screens.
 *
 * @param {ScreenMount} mountDashboardScreen - already imported by the caller
 * @param {ScreenMount} mountOverlay - the modal screen's mount function
 * @param {(message: string, tone?: string) => void} notify
 * @returns {(host: HTMLElement|null) => () => void}
 */
export function overDashboard(mountDashboardScreen, mountOverlay, notify) {
  return (host) => {
    const behind = mountDashboardScreen(host, { notify })
    const over = mountOverlay(host, { notify })
    return () => {
      over()
      behind()
    }
  }
}
